/**
 * AuthBootstrap — lightweight root-level auth provider.
 *
 * Runs once on mount to validate the stored token against the API and
 * populate user context. Contains NO Apollo dependency so that the
 * Apollo client (60 KB) stays out of the main JS bundle and only loads
 * when a user navigates to a protected dashboard route.
 */
import React, { useEffect } from "react";
import { useUserContext, ICredentials } from "./context/user-context";
import { appFetch } from "./helpers/app-fetch";
import history from "./history";
import { frontEnd } from "./config/links";
import Loading from "./components/loading/loading";

const AuthBootstrap: React.FunctionComponent<React.PropsWithChildren<{}>> = ({
  children
}) => {
  const { userState, setStateUserState } = useUserContext();

  useEffect(() => {
    getUser();

    async function getUser() {
      const token = localStorage.getItem("token");
      if (!token) return;

      let response;
      try {
        response = await appFetch("/api/auth/validate", { method: "GET" });
      } catch {
        localStorage.removeItem("token");
        return;
      }

      if (!response?.value) {
        localStorage.removeItem("token");
        const currentPath =
          (history as any).location?.pathname || window.location.pathname;
        const publicPaths = [
          "/terms",
          "/privacy-policy",
          "/cookie-policy",
          "/blog"
        ];
        const isPublicPage = publicPaths.some(
          p => currentPath === p || currentPath.startsWith(p + "/")
        );
        if (!isPublicPage) {
          history.push("/");
        }
        return;
      }

      localStorage.setItem("user", JSON.stringify({ email: response.value }));

      if (response.value.email !== userState.attributes.email) {
        setStateUserState({
          attributes: {
            email: response.value.email,
            firstName: response.value.firstName,
            lastName: response.value.lastName,
            roles: response.value?.roles || [],
            registered: response.value?.registered,
            registeredUntil: response.value?.registeredUntil,
            emailConfirmed: response.value?.emailConfirmed,
            referrer: userState?.attributes?.referrer ?? null,
            exam: response.value?.exam,
            exams: response.value?.exams,
            checkedIfUserLoggedIn: true
          }
        } as ICredentials);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!userState) return <Loading setSize={400} />;
  return <>{children}</>;
};

export default AuthBootstrap;
